import { IEditorV3, isIEditorV3 } from "@azu-business/editor-v3";
import { AitTableData, ArsListingBody, ArsPageByItem, ArsTableBody, FigureDataDto } from "aic";
import { AnnotationWhereGroup } from "components/annotations/AnnotationWhereGroup";
import { EditorWithAnnotation } from "components/annotations/interface";
import { v1FigureBody } from "components/tlfpage/V1Figure";
import { isVariantInformation, VariantInformation } from "functions/tlf/isVariantInformation";
import { eHistoryType, eTlfItemType } from "../enums";
import { isTitlesFootnotesReadDto, TitlesFootnotesReadDto } from "./TitlesFootnotesReadDto";

export interface ITlfItemHistory extends Record<string, unknown> {
  timestamp: string;
  id: number;
  updateType: eHistoryType;
  comment: string;
  userName: string;
}

/**
 * Type guard that asserts whether a value conforms to the ITlfItemHistory shape.
 *
 * @param obj - The object to validate as a TlfItemReadDto
 * @returns boolean
 */
export const isITlfItemHistory = (obj: unknown): obj is ITlfItemHistory => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;

  // Check basic props
  const chkObj = obj as ITlfItemHistory;
  if (!["string", "undefined"].includes(typeof chkObj.timestamp)) return false;
  if (chkObj.id === undefined || typeof chkObj.id !== "number") return false;
  if (chkObj.updateType === undefined || !Object.values(eHistoryType).includes(chkObj.updateType)) {
    return false;
  }
  if (chkObj.comment === undefined || typeof chkObj.comment !== "string") return false;
  if (chkObj.userName === undefined || typeof chkObj.userName !== "string") return false;

  return true;
};

// export interface TlfTableReadDto extends TitlesFootnotesReadDto {
// id: string;
// azsolId: string;
// version: number;

// primaryHierarchyId: string;
// primaryHierarchyName: string;
// primaryHierarchyType: eHierarchy;
// primaryHierarchyLevel: eHierarchyLevel;
// dNumber: string;

// tocOrder?: number;
// tocNumber?: string;
// tocLabel?: string;
// analysisSetId?: string;
// tocReleaseStatus?: eReleaseStatus;
// tocOriginTrafficLight?: MosaicTrafficLightsValue;
// coreTrafficLight: MosaicTrafficLightsValue;
// coreTrafficLightSwitch: MosaicTrafficLightSwitch;

// additionalInformation: string;
// type: eTlfItemType.table;
// domain: string;
// crystalReference?: string;

// programName: string;
// suffix: string;
// outputName: string;
// outputType: eTlfOutputType;
// sapReference: string;
// standardsNotes: IEditorV3;
// programmerNotes: IEditorV3;
// titles: AibBlockLine<IEditorV3>[];
// pageBy: EditorWithAnnotation<AnnotationWhereGroup>;
// body: AitTableData<IEditorV3>;
// footnotes: AibBlockLine<IEditorV3>[];
// releaseDate?: Date;
// decommissionDate?: Date;
// releaseStatus: eReleaseStatus;

// history: ITlfItemHistory[];
// relationships: ITlfItemRelationship[];
// shellMinorVersion: number;
// comments: CommentReadDto[];
// isBackupVersion: boolean;
// }

export interface TlfItemBaseReadDto extends TitlesFootnotesReadDto {
  crystalReference?: string;
  sapReference: string;
  standardsNotes: IEditorV3;
  programmerNotes: IEditorV3;
  history: ITlfItemHistory[];
}

export interface TlfArsListingReadDto extends TlfItemBaseReadDto {
  type: eTlfItemType.arsListing;
  body: ArsListingBody<IEditorV3>;
  pageBy: ArsPageByItem<IEditorV3>[];
}

export interface TlfArsTableReadDto extends TlfItemBaseReadDto {
  type: eTlfItemType.arsTable;
  body: ArsTableBody<IEditorV3>;
  pageBy: ArsPageByItem<IEditorV3>[];
}

export interface TlfFigureReadDto extends TlfItemBaseReadDto {
  type: eTlfItemType.figure;
  body: FigureDataDto;
  pageBy: EditorWithAnnotation<AnnotationWhereGroup>;
}

export interface TlfListingReadDto extends TlfItemBaseReadDto {
  type: eTlfItemType.listing;
  body: AitTableData<IEditorV3>;
  pageBy: EditorWithAnnotation<AnnotationWhereGroup>;
}

export interface TlfTableReadDto extends TlfItemBaseReadDto {
  type: eTlfItemType.table;
  body: AitTableData<IEditorV3>;
  pageBy: EditorWithAnnotation<AnnotationWhereGroup>;
}

export interface TlfV1FigureReadDto extends TlfItemBaseReadDto {
  type: eTlfItemType.v1figure;
  body: v1FigureBody;
  pageBy: EditorWithAnnotation<AnnotationWhereGroup>;
}

export type TlfArsTableMasterReadDto = TlfArsTableReadDto & VariantInformation;
export type TlfArsListingMasterReadDto = TlfArsListingReadDto & VariantInformation;
export type TlfArsMasterReadDto = TlfArsTableMasterReadDto | TlfArsListingMasterReadDto;

export type TlfItemReadDto =
  | TlfArsListingReadDto
  | TlfArsTableReadDto
  | TlfArsListingMasterReadDto
  | TlfArsTableMasterReadDto
  | TlfFigureReadDto
  | TlfListingReadDto
  | TlfTableReadDto
  | TlfV1FigureReadDto;

/**
 * Type guard for the rich-text PageBy shape used by non-ARS TLF items.
 *
 * @param obj - Unknown pageBy value
 * @returns True when value is the editor+annotation pageBy structure
 */
export const isEditorPageBy = (obj: unknown): obj is EditorWithAnnotation<AnnotationWhereGroup> => {
  if (typeof obj !== "object" || obj === null) return false;
  return isIEditorV3((obj as EditorWithAnnotation<AnnotationWhereGroup>).content);
};

/**
 * Type guard for a single ARS page-by item.
 *
 * @param obj - Unknown page-by entry
 * @returns True when the value is a valid ARS page-by item
 */
export const isArsPageByItem = (obj: unknown): obj is ArsPageByItem<IEditorV3> => {
  if (typeof obj !== "object" || obj === null) return false;

  const chkObj = obj as ArsPageByItem<IEditorV3>;
  if (typeof chkObj.groupingFactorId !== "string") return false;
  if (chkObj.label !== undefined && !isIEditorV3(chkObj.label)) return false;

  return true;
};

/**
 * Type guard that asserts whether a value conforms to the TlfItemReadDto shape.
 *
 * @param obj - The object to check
 * @returns true if the object is a TlfItemReadDto, false otherwise
 */
export const isTlfItemReadDto = (obj: unknown): obj is TlfItemReadDto => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;

  // Check lower interface
  if (!isTitlesFootnotesReadDto(obj)) return false;

  // Check basic props
  const chkObj = obj as TlfItemReadDto;
  if (!["undefined", "string"].includes(typeof chkObj.crystalReference)) return false;
  if (typeof chkObj.sapReference !== "string") return false;
  if (!isIEditorV3(chkObj.standardsNotes)) return false;
  if (!isIEditorV3(chkObj.programmerNotes)) return false;
  if (typeof chkObj.body !== "object") return false;

  // Check arrays
  if (!Array.isArray(chkObj.history) || chkObj.history.some((x) => !isITlfItemHistory(x)))
    return false;
  if (chkObj.pageBy === undefined) return false;
  if (
    (chkObj.type === eTlfItemType.arsListing || chkObj.type === eTlfItemType.arsTable) &&
    (!Array.isArray(chkObj.pageBy) || chkObj.pageBy.some((item) => !isArsPageByItem(item)))
  )
    return false;
  if (
    chkObj.type !== eTlfItemType.arsListing &&
    chkObj.type !== eTlfItemType.arsTable &&
    !isEditorPageBy(chkObj.pageBy)
  )
    return false;

  // Everything is good
  return true;
};

/**
 * Type guard for arsTableMasterReadDto
 *
 * @param obj - The object to check
 * @returns True when the value is a valid master Ars table template
 */
export const isArsTableMasterReadDto = (obj: unknown): obj is TlfArsTableMasterReadDto => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;
  // Check extended TlfItemReadDto properties
  if (!isTlfItemReadDto(obj) || obj.type !== eTlfItemType.arsTable) return false;
  if (!isVariantInformation(obj)) return false;

  // Everything is good
  return true;
};

/**
 * Type guard for ARS listings that carry variant patch metadata.
 *
 * @param obj - The value to check
 * @returns True when the value is a valid ARS listing master
 */
export const isArsListingMasterReadDto = (obj: unknown): obj is TlfArsListingMasterReadDto => {
  if (!isTlfItemReadDto(obj) || obj.type !== eTlfItemType.arsListing) return false;
  return isVariantInformation(obj);
};

/**
 * Type guard for either ARS item type carrying variant patch metadata.
 *
 * @param obj - The value to check
 * @returns True when the value is a valid ARS master item
 */
export const isArsMasterReadDto = (obj: unknown): obj is TlfArsMasterReadDto =>
  isArsTableMasterReadDto(obj) || isArsListingMasterReadDto(obj);
